import { useRef, useState } from "react"
import { faEye, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Modal, Pagination, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { XlsForm } from "interfaces/dataInterfaces"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { useForm } from "react-hook-form"
import { GET_ITEM_LIMIT, RPME_MODULE } from "constants/constants"
import {
  useGetXlsFormQuery,
  useDeleteXlsFormSchemaMutation,
  useGetXlsFormSchemasByIdQuery,
  useDownloadXlsFormSchemasByIdMutation,
  useDownloadJsonSchemasByIdMutation,
} from "services/rpme/xlsFormSchemas"
import HeaderCTA from "components/Shared/HeaderFilters/HeaderCTA"
import { useNavigate } from "react-router-dom"
import { formatDate } from "utils/helpers"
import DetailsModal from "pages/BaseXlsForm/DetailsModal"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"

type Inputs = {
  name: string
  localName: string
}

const permissionsProps = {
  moduleId: RPME_MODULE,
  subModule: "xlsForms",
}

export default function RPMEXlsForm() {
  const navigate = useNavigate()
  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(GET_ITEM_LIMIT)
  useForm<Inputs>()
  const [actionItem, setActionItem] = useState<{
    item: XlsForm
    action: "edit" | "delete" | "details"
  } | null>(null)

  const [admCode, setAdmCode] = useState(0)
  const [query, setQuery] = useState("")
  const [isSearchMode, setIsSearchMode] = useState(false)
  const { data: xlsData } = useGetXlsFormQuery({
    adm0Code: admCode,
    search: query,
    skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
    take: getItemLimit,
  })
  const [deleteXlsFormSchema] = useDeleteXlsFormSchemaMutation()

  const { data: xls, isLoading: loadingXLS } = useGetXlsFormSchemasByIdQuery(actionItem?.item.xlsFormId ?? 0, {
    skip: actionItem?.action !== "details",
  })

  const [download] = useDownloadXlsFormSchemasByIdMutation()
  const [downloadJson, { isLoading: loadingJson }] = useDownloadJsonSchemasByIdMutation()

  const handleDelete = async (id: number) => {
    await deleteXlsFormSchema(id)
    setActionItem(null)
  }

  const inputRef = useRef<TextInput>(null)
  const handleSearch = () => {
    const q = (inputRef.current as any).value as string
    if (!q.length) return
    setQuery(q)
    setIsSearchMode(true)
    setPageNumber(1)
  }
  const handleClearSearch = () => {
    const input = inputRef.current as any
    input.value = ""
    setQuery("")
    setIsSearchMode(false)
    setPageNumber(1)
  }

  const getadata = async (event: any) => {
    setAdmCode(event?.value || 0)
    setPageNumber(1)
  }

  const columns: Array<ColumnShape<XlsForm>> = [
    {
      key: "xlsFormId",
      dataKey: "xlsFormId",
      title: "ID",
      width: 80,
    },
    {
      key: "countryName",
      dataKey: "countryName",
      title: "Country",
      width: 140,
    },
    {
      key: "xlsFormName",
      dataKey: "xlsFormName",
      title: "XLSForm Name",
      width: 200,
    },
    {
      key: "baseXlsFormName",
      dataKey: "baseXlsFormName",
      title: "Base XLSForm Name",
      width: 240,
    },
    {
      key: "xlsFormCreateDate",
      // dataKey: "xlsFormCreateDate",
      title: "Created Date",
      width: 150,
      cellRenderer: ({ rowData }) => formatDate(rowData.xlsFormCreateDate),
    },
    {
      key: "userName",
      dataKey: "userName",
      title: "Created By",
      width: 250,
    },
    {
      key: "action",
      title: "Action",
      width: 250,
      // flexGrow: 1,
      align: Column.Alignment.CENTER,
      cellRenderer: ({ rowData }) => (
        <>
          <PermissionWrapper {...permissionsProps} feature="details">
            <Button
              action={() =>
                setActionItem({
                  item: {
                    ...rowData,
                  },
                  action: "details",
                })
              }
              label="Details"
              className="btn-full blue action-btn"
              icon={faEye}
            />
          </PermissionWrapper>
          <PermissionWrapper {...permissionsProps} feature="delete">
            <Button
              action={() =>
                setActionItem({
                  item: {
                    ...rowData,
                  },
                  action: "delete",
                })
              }
              label="Delete"
              className="btn-full danger action-btn"
              style={{ marginLeft: "5px" }}
              icon={faTrash}
            />
          </PermissionWrapper>
        </>
      ),
    },
  ]
  return (
    <>
      <HeaderFilters
        left={
          <>
            <CountrySelector onSelect={getadata} />

            <TextInput
              name="query"
              placeholder="Type here to filter results..."
              type="text"
              style={{ fontSize: "0.875rem", height: 33, borderRadius: 4 }}
              ref={inputRef}
            />
            <Button
              action={handleSearch}
              label="Search"
              className="btn-full blue"
              icon={faSearch}
              style={{ marginLeft: "10px" }}
            />
            {isSearchMode && (
              <Button
                action={handleClearSearch}
                label="Clear"
                className="btn-full blue"
                style={{ marginLeft: "8px" }}
              />
            )}
          </>
        }
        right={
          <PermissionWrapper {...permissionsProps} feature="addNew">
            <HeaderCTA onClick={() => navigate("add")} label="Add New XLS Form Schema" />
          </PermissionWrapper>
        }
      />
      <div style={{ height: "100%", minHeight: "60vh", marginTop: "10px" }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={xlsData?.items}
              width={width}
              height={height}
              rowKey="xlsFormId"
              className="custom-table"
              rowHeight={38}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
            />
          )}
        </AutoResizer>
        <Modal
          open={actionItem?.action === "delete"}
          modalHeading="Confirm Delete"
          primaryButtonText="Delete"
          secondaryButtonText="Cancel"
          onRequestSubmit={() => handleDelete(actionItem!.item.xlsFormId)}
          onRequestClose={() => setActionItem(null)}
        >
          Are you sure you want to delete the selected record?
        </Modal>
      </div>{" "}
      <Pagination
        totalItems={xlsData?.count ?? 0}
        pageSize={getItemLimit}
        pageSizes={[10, 20, 30]}
        page={pageNumber}
        onChange={(event: any) => {
          setPageNumber((event as any).page)
          setGetItemLimit((event as any).pageSize)
        }}
      />
      {actionItem?.action === "details" && (
        <DetailsModal
          xls={xls}
          loading={loadingXLS || loadingJson}
          download={download}
          downloadJSON={downloadJson}
          isOpen={actionItem.action === "details"}
          onClose={() => setActionItem(null)}
          module="rpme"
        />
      )}
    </>
  )
}
